import {
  checkboxFilter,
  dateRangeFilter,
  DateRangeFilterValue,
  FilterDefs,
  GroupByHook,
  singleFilter,
  useGridTableLayoutState,
  useSessionStorage,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { DateOperation, PlanTasksFilter, TaskStatus, TradePartnerTaskStatus } from "src/generated/graphqlTypes";
import { StringParam, useQueryParams } from "use-query-params";
import { DynamicScheduleView } from "../utils";

export enum TaskGroupBy {
  None = "None",
  CostCode = "CostCode",
  Stage = "Stage",
  Lookahead = "Lookahead",
}

enum TradePartnerStatusFilter {
  NotScheduled = "NotScheduled",
  Scheduled = "Scheduled",
}

export type DraftScheduleFilter = {
  startDateRange?: DateRangeFilterValue<DateOperation>;
  endDateRange?: DateRangeFilterValue<DateOperation>;
  includeCompletedTasks?: boolean;
  includeMilestones?: boolean;
  tradePartnerStatus?: TradePartnerStatusFilter;
};

const ganttGroupByOptions = [
  { id: TaskGroupBy.None, name: "None" },
  { id: TaskGroupBy.CostCode, name: "Cost Code" },
  { id: TaskGroupBy.Stage, name: "Stage" },
];
// The gantt can't show the lookahead's week dividers, so only the list offers it
const listGroupByOptions = [...ganttGroupByOptions, { id: TaskGroupBy.Lookahead, name: "Lookahead" }];

const tradePartnerStatusOptions = [
  { value: TradePartnerStatusFilter.NotScheduled, label: "Trade Not Scheduled" },
  { value: TradePartnerStatusFilter.Scheduled, label: "Trade Scheduled" },
];

const draftScheduleFilterDefs: FilterDefs<DraftScheduleFilter> = {
  startDateRange: dateRangeFilter({
    label: "Start Date Range",
    // Shown as the field's inline label. Beam defaults it to "Date", which makes the two ranges look the same.
    testFieldLabel: "Start",
    defaultValue: { op: DateOperation.Between, value: undefined },
  }),
  endDateRange: dateRangeFilter({
    label: "Finish Date Range",
    // Shown as the field's inline label. Beam defaults it to "Date", which makes the two ranges look the same.
    testFieldLabel: "Finish",
    defaultValue: { op: DateOperation.Between, value: undefined },
  }),
  includeCompletedTasks: checkboxFilter({ label: "Show Completed" }),
  includeMilestones: checkboxFilter({ label: "Show Milestones" }),
  tradePartnerStatus: singleFilter({
    label: "Trade Partner Status",
    options: tradePartnerStatusOptions,
    getOptionValue: (o) => o.value,
    getOptionLabel: (o) => o.label,
  }),
};

/**
 * Filter, search, and group by state shared by every draft schedule view.
 *
 * It lives at the page level so switching between the List, Calendar, and Gantt tabs keeps the same filters and
 * grouping. Search is "server" mode because we filter the plan tasks ourselves in `filterPlanTasksAndMilestones`,
 * which also feeds the calendar and gantt views.
 */
export function useDraftScheduleLayoutState(view: DynamicScheduleView) {
  const layoutState = useGridTableLayoutState<DraftScheduleFilter>({
    persistedFilter: { filterDefs: draftScheduleFilterDefs, storageKey: "draftScheduleFilter" },
    search: "server",
  });
  const { setSearchString } = layoutState;
  const groupBy = useDraftScheduleGroupBy(view);
  const [{ search: searchParam }] = useQueryParams({ search: StringParam });

  // Beam fills its search box from ?search= but only reports typing, so a deep link searches from the URL
  // TODO: Beam gap (DS-377): use just `layoutState.searchString` once Beam is bumped past DS-377, which applies
  // ?search= on load
  const search = layoutState.searchString || searchParam || "";

  const previousView = useRef(view);
  useEffect(() => {
    if (previousView.current === view) return;
    previousView.current = view;
    // The tab links carry the search in ?search=, so drop the old view's typed search and read it from the URL
    setSearchString("");
  }, [view, setSearchString]);

  return {
    layoutState: {
      ...layoutState,
      // The milestone view only supports search, so hide the filters there.
      filterDefs: view === DynamicScheduleView.Milestone ? undefined : layoutState.filterDefs,
      groupBy,
    },
    filter: layoutState.filter,
    search,
    groupBy: (groupBy?.value as TaskGroupBy | undefined) ?? TaskGroupBy.None,
  };
}

/**
 * The draft schedule's group by, kept in sessionStorage. Only the list and gantt views can group.
 *
 * Beam's group by only lives in `?groupBy=`, which the view tabs, Add Task, and Publish all drop. Keeping it in
 * sessionStorage keeps the grouping when moving between them, like the old filter did.
 */
function useDraftScheduleGroupBy(view: DynamicScheduleView): GroupByHook<string> | undefined {
  const [storedValue, setValue] = useSessionStorage<string>(draftScheduleGroupByKey, TaskGroupBy.None);
  const options =
    view === DynamicScheduleView.List
      ? listGroupByOptions
      : view === DynamicScheduleView.Gantt
        ? ganttGroupByOptions
        : undefined;
  // Show None for a value this view doesn't offer, but keep it stored so the list's Lookahead comes back after the
  // gantt. This also stops a bad stored value from breaking the table.
  const value = options?.some((o) => o.id === storedValue) ? storedValue : TaskGroupBy.None;
  return useMemo(() => (options ? { value, setValue, options } : undefined), [options, value, setValue]);
}

/** Groups the list by Lookahead, for the redirect from the old Lookahead tab. */
export function useSetLookaheadGroupBy() {
  const [, setValue] = useSessionStorage<string>(draftScheduleGroupByKey, TaskGroupBy.None);
  return useCallback(() => setValue(TaskGroupBy.Lookahead), [setValue]);
}

const draftScheduleGroupByKey = "draftScheduleGroupBy";

export type DraftScheduleLayoutState = ReturnType<typeof useDraftScheduleLayoutState>["layoutState"];

export function mapToFilter(filter: DraftScheduleFilter): PlanTasksFilter {
  const { startDateRange, endDateRange, includeCompletedTasks, tradePartnerStatus } = filter;
  // There is no status filter in the UI
  const nonComplete = Object.values(TaskStatus).filter((status) => status !== TaskStatus.Complete);
  return {
    ...(startDateRange?.value?.from && startDateRange.value.to
      ? {
          startDateRange: {
            op: DateOperation.Between,
            value: startDateRange.value.from,
            value2: startDateRange.value.to,
          },
        }
      : {}),
    ...(endDateRange?.value?.from && endDateRange.value.to
      ? { endDateRange: { op: DateOperation.Between, value: endDateRange.value.from, value2: endDateRange.value.to } }
      : {}),
    status: [...nonComplete, ...(includeCompletedTasks ? [TaskStatus.Complete] : [])],
    tradePartnerStatus: tradePartnerStatus ? tradeStatusFilterToStatuses[tradePartnerStatus] : undefined,
  };
}

const tradeStatusFilterToStatuses: Record<TradePartnerStatusFilter, TradePartnerTaskStatus[]> = {
  [TradePartnerStatusFilter.NotScheduled]: [
    TradePartnerTaskStatus.NotSent,
    TradePartnerTaskStatus.NeedsConfirmation,
    TradePartnerTaskStatus.NeedsReconfirmation,
    TradePartnerTaskStatus.Unavailable,
  ],
  [TradePartnerStatusFilter.Scheduled]: [TradePartnerTaskStatus.CompletedJob, TradePartnerTaskStatus.Confirmed],
};
